import { Box, Button, Container, Grid, Typography, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import waspIcon from '../../assets/home/wasp_icon.svg';
import waspIconFlip from '../../assets/home/wasp_icon_flip.svg';
import gearIcon from '../../assets/home/gear_icon.svg';
import { useInView } from '../../hooks/useInView';

const InfoSection = () => {
  const leftWaspRef = useInView({ threshold: 0.5 });
  const rightWaspRef = useInView({ threshold: 0.5 });

  return (
    <Box 
      sx={{ 
        py: 8, 
        bgcolor: 'background.paper',
        '@keyframes moveIconFromLeft': {
          '0%': {
            opacity: 0,
            transform: 'translate(-300%, -45%) translateZ(0)'
          },
          '70%': {
            opacity: 1
          },
          '100%': {
            opacity: 1,
            transform: 'translate(-50%, -45%) translateZ(0)'
          }
        },
        '@keyframes moveIconFromRight': {
          '0%': {
            opacity: 0,
            transform: 'translate(200%, -45%) translateZ(0)'
          },
          '70%': {
            opacity: 1
          },
          '100%': {
            opacity: 1,
            transform: 'translate(-50%, -45%) translateZ(0)'
          }
        },
      }}
    >
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom 
          align="center" 
          sx={{ mb: 6, fontWeight: 'bold' }}
        >
          Insect Sting Allergy Prevention
        </Typography>

        <Grid container spacing={4}>
          {/* Material Section */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                height: '100%', 
                borderRadius: 2,
                '@media (hover: hover)': {
                  '&:hover .info-icon': {
                    transform: 'scale(1.15) translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                  }
                }
              }}
            >
              <Box sx={{ position: 'relative', height: 80, mb: 2 }}>
                {/* Stationary Background Circle */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 80,
                    height: 80,
                    bgcolor: '#e8f5e9',
                    border: '2px solid #a5d6a7',
                    borderRadius: '50%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1
                  }}
                />
                
                {/* Animated Icon */}
                <Box 
                  ref={leftWaspRef.ref}
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -45%)',
                    opacity: 0,
                    animation: leftWaspRef.isInView ? 'moveIconFromLeft 1.2s ease-out forwards' : 'none',
                    zIndex: 2
                  }}
                >
                  <img 
                    className="info-icon"
                    src={waspIconFlip}
                    alt="Wasp Icon" 
                    style={{ 
                      height: '60px', 
                      transition: 'transform 0.3s ease-in-out',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Material
              </Typography>
              <Typography>
              Wear looser fitting garments that have a smooth finish to the material. Cotton, nylon, khaki and sometime chambray can be better choices for preventing the stinger of the insect to reach your skin. Avoiding textured materials.  Avoid animal based material such as wool, fur, or feathers. 
              </Typography>
            </Paper>
          </Grid>

          {/* Avoid Stings Section */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                height: '100%', 
                borderRadius: 2,
                '@media (hover: hover)': {
                  '&:hover .info-icon': {
                    transform: 'scale(1.15) translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                  }
                }
              }}
            >
              <Box sx={{ position: 'relative', height: 80, mb: 2 }}>
                {/* Stationary Background Circle */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 80,
                    height: 80,
                    bgcolor: '#fff8e1',
                    border: '2px solid #ffe082',
                    borderRadius: '50%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1
                  }}
                />
                
                {/* Animated Icon */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -45%)',
                    opacity: 1,
                    zIndex: 2
                  }}
                >
                  <img 
                    className="info-icon"
                    src={gearIcon} 
                    alt="Gear Icon" 
                    style={{ 
                      height: '60px',
                      transition: 'transform 0.3s ease-in-out',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Avoid Stings
              </Typography>
              <Typography>
              Avoiding stings from honeybees, wasps, hornets and yellow jackets will lead to a more enjoyable outdoor experience for everyone. Stinging insects are most active during the late spring, summer, and early fall. Insect repellents do not work against stinging insects.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                component={RouterLink} 
                to="/clothing-guide"
                sx={{ mt: 2 }}
                size="small"
              >
                Clothing Guide
              </Button>
            </Paper>
          </Grid>

          {/* Color Combinations Section */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                height: '100%', 
                borderRadius: 2,
                '@media (hover: hover)': {
                  '&:hover .info-icon': {
                    transform: 'scale(1.15) translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                  }
                }
              }}
            >
              <Box sx={{ position: 'relative', height: 80, mb: 2 }}>
                {/* Stationary Background Circle */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 80,
                    height: 80,
                    bgcolor: '#e8f5e9',
                    border: '2px solid #a5d6a7',
                    borderRadius: '50%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1
                  }}
                />
                
                {/* Animated Icon */}
                <Box 
                  ref={rightWaspRef.ref}
                  sx={{ 
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -45%)',
                    opacity: 0,
                    animation: rightWaspRef.isInView ? 'moveIconFromRight 1.2s ease-out forwards' : 'none',
                    zIndex: 2
                  }}
                >
                  <img 
                    className="info-icon"
                    src={waspIcon} 
                    alt="Wasp Icon" 
                    style={{ 
                      height: '60px',
                      transition: 'transform 0.3s ease-in-out',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Color Combinations
              </Typography>
              <Typography>
              Picking the color of your apparel can be tricky.  Stinging insects can be more attracted to bold colors. Dark colors such as red, black and dark dark blue can be aggravating, especially if you are near a nest, causing  a sting situation.  Wearing white or light colors may reduce the chance of a sting if you are near a nest.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default InfoSection; 